import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon, Typography, useInterval } from '@packages/ui/shared';
import { Collapse } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';
import { FeedbackGroup, FeedbackGroupType, InvestigationModel, NotificationStatusId } from '@packages/models/api';
import { getLogger } from '@packages/core/logging';
import { isPrimaryObjectiveEnabled } from '@web/config/constants';
import { NavigationItemHoverTitle } from './navigation-item-hover-title';
import { setVin, useBmisDispatch, useBmisState } from '@packages/contexts/bmis';
import { useAbortController } from '@packages/core/http';
import { useAdvancedSearchContext } from '@packages/contexts/qmrs';
import { useAuthState } from '@packages/contexts/auth';
import { usePermissionsState } from '@packages/contexts/permissions';
import { useLocation, useMatch } from 'react-router-dom';
import { useSummariesState } from '@web/core/hooks/use-summaries';
import { useTranslation } from 'react-i18next';
import {
    fetchPublicationsCount,
    fetchQmrsCount,
    useVinSessionDispatch,
    useVinSessionState,
} from '@web/vin-sessions/context/vin-sessions.hooks';
import {
    notificationsService,
    bmisService,
    feedbackService,
    investigationService,
    qmrsService,
    cvtService,
    vinSessionService,
    dmsService,
} from '@web/services/singletons';
import {
    fetchNotificationsSummary,
    fetchNotificationsList,
    useNotificationsDispatch,
    useNotificationsState,
} from '@packages/contexts/notifications';
import colors from '@packages/core/styles/colors';
import NavigationItem from '@web/components/navigation-item';
import NavigationSubItem from '@web/components/navigation-sub-item';
import useQuery from '@web/core/hooks/use-query';
import { useCvt } from '@packages/contexts/cvt';
import { useFeatureFlags } from '@packages/contexts/feature-flags';
import { canAccessSTIS } from '@web/utils/can-view-stis';
import { AccountManagerIcon } from '@web/vin-sessions/assets/Icons';
import { isNavRouteActive } from '@web/components/is-nav-route-active';
import { usePublicationsState } from '@web/vin-sessions/components/stis-publications/context/publications.hooks';
import { dmsActions, useDmsDispatch, useDmsState } from '@packages/contexts/dms';
import { useCidCvnState } from '@packages/contexts/cid-cvn';

const logger = getLogger('web');

type ExpandableNavItems = 'qmr' | 'feedback' | 'investigations' | 'administration' | 'techline' | 'vinQmr';
const useNavigationStyles = createUseStyles({
    navigationOuter: ({ open }: { open: boolean }) => ({
        display: 'flex',
        position: 'relative',
        width: open ? 248 : 48,
        transition: '0.2s width',
    }),
    navigation: {
        display: 'flex',
        minWidth: '100%',
        padding: '16px 0 0 0',
        overflowY: 'auto',
        overflowX: 'hidden',
        flexDirection: 'column',
        backgroundColor: colors.grayTwo,
    },
    navigationSubItemCollapseButton: {
        border: 0,
        borderRadius: 0,
        display: 'block',
        padding: '10px 0',
        appearance: 'none',
        backgroundColor: 'transparent',
    },
    collapseButtonOuter: {
        position: 'absolute',
        bottom: 32,
        right: -12,
    },
    featureFlagDemoTag: {
        margin: '8px 12px',
        padding: '6px 8px',
        border: `1px dashed ${colors.grayFour}`,
        borderRadius: 6,
        color: colors.graySix,
        backgroundImage:
            'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(0, 0, 0, 0.06) 8px, rgba(0, 0, 0, 0.03) 5px)',
        fontSize: 12,
        fontFamily: 'Roboto',
        fontWeight: 500,
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
    },
    collapseButton: {
        width: 24,
        height: 24,
        display: 'flex',
        borderRadius: '50%',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.blueFour,
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.16)',
    },
});

interface NavigationProps {
    open: boolean;
    onOpenTogglePress(open: boolean): void;
}
const Navigation: FC<NavigationProps> = ({ open, onOpenTogglePress }) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { isFeatureFlagDemoFeatureEnabled, isSsm5DiagnosticsFeatureEnabled } = useFeatureFlags();
    const landingRouteMatch = useMatch('/');
    const authRouteMatch = useMatch('/auth');
    const onboardingRouteMatch = useMatch('/onboard');
    const stisOnBoardingRouteMatch = useMatch('/stis/onboard');
    const pspdfNavigateRouteMatch = useMatch('/preview/*');
    const adminRouteMatch = useMatch('/administration');
    const adminWildcardRouteMatch = useMatch('/administration/*');
    const qmrRouteMatch = useMatch('/qmrs');
    const qmrWildcardRouteMatch = useMatch('/qmrs/*');
    const qmrDetailRouteMatch = useMatch('/qmrs/:id');
    const qmrEditRouteMatch = useMatch('/qmrs/:id/edit');
    const nqrDetailRouteMatch = useMatch('/nqrs/:id');
    const nqrEditRouteMatch = useMatch('/nqrs/:id/edit');
    const qmrsSlashDashboard = useMatch('/qmrs/dashboard/*');
    const nqQMRRouteMatch = useMatch('/nqrs');

    const bmisDetailRouteMatch = useMatch('/vin/:vin/bmis/:rowGuid');
    const dmsRouteMatch = useMatch('/vin/:vin/repair-orders/:id');
    const stisRouteMatch = useMatch('/vin/:vin/stis/*');
    const stisAltRouteMatch = useMatch('/stis/*');
    const serviceDiagnosticsRouteMatch = useMatch('/service-diagnostics/*');
    const savedSearchRouteMatch = useMatch('/qmrs/advanced-search/saved');
    const advancedSearchRouteMatch = useMatch('/qmrs/advanced-search/search');
    const advancedSearchResultRouteMatch = useMatch('/qmrs/advanced-search/:searchId');
    const advancedSearchSavedResultsRouteMatch = useMatch('/qmrs/advanced-search/saved/:searchId');
    const techlineDashboardRouteMatch = useMatch('/techline/dashboard');
    const techlineAllCasesRouteMatch = useMatch('/techline/all-cases');
    const techlineDashboardV2RouteMatch = useMatch('/techline/dashboardv2');
    const worksheetPreviewRouteMatch = useMatch('/administration/worksheet/preview/:worksheetId');
    const techlineNewOpenCasesRouteMatch = useMatch('/techline/:caseNumber');
    const isActualNewOpenCasesRouteMatch =
        techlineNewOpenCasesRouteMatch &&
        techlineNewOpenCasesRouteMatch?.params?.caseNumber !== 'dashboard' &&
        techlineNewOpenCasesRouteMatch?.params?.caseNumber !== 'dashboardv2' &&
        techlineNewOpenCasesRouteMatch?.params?.caseNumber !== 'all-cases';
    const techlineOpenRouteMatch = useMatch('/techline/open');
    const techlineRouteMatch =
        !!techlineDashboardRouteMatch ||
        !!techlineAllCasesRouteMatch ||
        !!isActualNewOpenCasesRouteMatch ||
        !!techlineOpenRouteMatch;
    const investigationsRouteMatch = useMatch('/investigations');
    const investigationsWildcardRouteMatch = useMatch('/investigations/*');
    const escalationsRouteMatch = useMatch('/escalations');
    const escalationsWildcardRouteMatch = useMatch('/escalations/*');
    const investigationsDetailRouteMatch = useMatch('/investigations/:id');
    const feedbackRouteMatch = useMatch('/feedback');
    const feedbackWildcardRouteMatch = useMatch('/feedback/*');
    const manageDocumentationRouteMatch = useMatch('/user-articles/*');
    const vinSessionRouteMatch = useMatch('/vin/*');
    const bmisRouteMatch = useMatch('/vin/:id/bmis/*');
    const vinCidCvnCheckRouteMatch = useMatch('/vin/:id/cid-cvn-check');
    const vinRepairOrderRouteMatch = useMatch('/vin/:id/repair-orders');
    const vinCampaignsRouteMatch = useMatch('/vin/:id/campaigns');
    const vinCidCvnCheckReportRouteMatch = useMatch('/vin/:id/cid-cvn-check/:id');
    const vinQmrRouteMatch = useMatch('/vin/:id/qmrs');
    const vinNqQMRRouteMatch = useMatch('/vin/:id/nqrs');
    const vinTechlineRouteMatch = useMatch('/vin/:id/techline');
    const vinTechlineCidCvnWorksheetRouteMatch = useMatch(`/vin/:id/techline/:caseNumber/cid-cvn-worksheet`);
    const vinDashboardRouteMatch = useMatch('/vin/:id');
    const vinEscalationRouteMatch = useMatch('/vin/:id/escalations');
    const vinInvestigationsRouteMatch = useMatch('/vin/:id/investigations');
    const { canAccessStis } = usePublicationsState();

    const { account } = useAuthState();
    const [displayFlag, setDisplayFlag] = useState<boolean>(true);
    const dmsDispatch = useDmsDispatch();
    const { hasHistoryFetched, dmsCount, campaignCount, hasCampaignHistoryFetched } = useDmsState();
    const notificationsDispatch = useNotificationsDispatch();
    const { notificationSummary } = useNotificationsState();
    const prevNewCountRef = useRef<number | undefined>(undefined);

    // Shared Contexts
    const { recentSavedSearches, setRecentSavedSearches } = useAdvancedSearchContext();
    const { vinQmrsCount, vinTechlineCasesCount, publicationsCount, hasPublicationsCountFetched, hasQmrsCountFetched } =
        useVinSessionState();
    const { selectedVin } = useVinSessionState();
    const vinDispatch = useVinSessionDispatch();

    const bmisDispatch = useBmisDispatch();

    useEffect(() => {
        if (selectedVin) {
            if (!hasPublicationsCountFetched) {
                fetchPublicationsCount(vinDispatch, vinSessionService, selectedVin);
            }
            if (!hasQmrsCountFetched) {
                fetchQmrsCount(vinDispatch, vinSessionService, selectedVin?.vinRecord.vin);
            }
        }
    }, [selectedVin, hasPublicationsCountFetched, hasQmrsCountFetched]);

    // Reset DMS flags/data when VIN changes so hasHistoryFetched flips back to false
    const prevVinRef = useRef<string | undefined>();
    useEffect(() => {
        const vin = selectedVin?.vinRecord?.vin;
        if (prevVinRef.current !== vin) {
            dmsDispatch(dmsActions.clearDmsData());
            prevVinRef.current = vin;
        }
    }, [selectedVin?.vinRecord?.vin, dmsDispatch]);

    useEffect(() => {
        const vin = selectedVin?.vinRecord?.vin;
        if (!vin || hasHistoryFetched) return;
        dmsService.fetchAndSetRepairOrderHistory(vin, dmsDispatch);
    }, [selectedVin?.vinRecord?.vin, hasHistoryFetched]);

    useEffect(() => {
        const vin = selectedVin?.vinRecord?.vin;
        if (!vin || hasCampaignHistoryFetched) return;
        dmsService.fetchAndSetCampaignsByVin(vin, dmsDispatch);
    }, [selectedVin?.vinRecord?.vin, hasCampaignHistoryFetched]);

    useEffect(() => {
        setVin(bmisDispatch, bmisService, { vin: selectedVin?.vinRecord?.vin, ignoreCache: false });
    }, [bmisDispatch, selectedVin?.vinRecord?.vin]);

    const { bmisData } = useBmisState();
    const { state: cvtState } = useCvt(cvtService);
    const { cvtList } = cvtState;

    const { cidCvnCount } = useCidCvnState();

    const selectedVinString = useMemo(() => selectedVin?.vinRecord?.vin ?? '', [selectedVin]);

    // Vin Session Logic
    const cvtReportRouteMatch = useMatch(`/vin/${selectedVinString}/cvt/:id`);
    const cvtRouteMatch = useMatch(`/vin/${selectedVinString}/cvt`);

    const query = useQuery();
    const queryGroupType = query.get('groupType') || FeedbackGroupType.All;
    const queryEntityId = query.get('entityId');
    const { fetchQmrSummaries } = useSummariesState();
    const [savedSearchesOpen, setSavedSearchesOpen] = useState(true);
    const { permissions } = usePermissionsState();
    const [recentInvestigations, setRecentInvestigations] = useState<InvestigationModel[]>([]);
    const hideNavigation = !!qmrsSlashDashboard
        ? false
        : authRouteMatch ||
          onboardingRouteMatch ||
          stisOnBoardingRouteMatch ||
          pspdfNavigateRouteMatch ||
          qmrDetailRouteMatch ||
          qmrEditRouteMatch ||
          nqrDetailRouteMatch ||
          nqrEditRouteMatch ||
          bmisDetailRouteMatch ||
          dmsRouteMatch ||
          manageDocumentationRouteMatch ||
          !!isActualNewOpenCasesRouteMatch ||
          worksheetPreviewRouteMatch ||
          !!cvtReportRouteMatch ||
          !!vinTechlineCidCvnWorksheetRouteMatch ||
          !!vinCidCvnCheckReportRouteMatch;

    const classes = useNavigationStyles({ open });
    const { abortSignalRef } = useAbortController();
    const [feedbackGroups, setFeedbackGroups] = useState<FeedbackGroup[]>([]);
    const [hoveredMap, setHoveredMap] = useState<{ [key: string]: boolean }>({});
    const [currentHover, setCurrentHover] = useState('');
    const [currentRef, setCurrentRef] = useState<HTMLElement>();
    const [userToggledNavItems, setUserToggledNavItems] = useState<Partial<Record<ExpandableNavItems, boolean>>>({});
    const qaNavRouteActive = isNavRouteActive('qa', pathname);

    const isNavGroupOpen = useCallback(
        (item: ExpandableNavItems) => userToggledNavItems[item] ?? (item === 'qmr' && qaNavRouteActive),
        [userToggledNavItems, qaNavRouteActive]
    );

    const showQmrsDashboard = useMemo(() => {
        const eligiblePermissions: string[] = ['DASHBOARD001'];
        const hasPermissions = permissions?.filter(
            (permission: any) => eligiblePermissions.indexOf(permission.permissionId) > -1
        );
        return hasPermissions && hasPermissions?.length > 0;
    }, [permissions]);

    const canViewBmisInVinSession = Boolean(account?.bmisCapabilities?.canViewBatteryTestReport);
    const canViewCidCvnCheck = Boolean(account?.cidCvnCapabilities?.canViewCIDCVNList);
    const canViewFeedback = Boolean(account?.feedbackCapabilities?.giveOrRequestFeedback);
    const canViewQmrs = Boolean(account?.systemCapabilities?.canViewQualityAssurance);
    const canViewTechline = Boolean(account?.systemCapabilities?.canViewTechline);

    const isVinCvtEligible = Boolean(selectedVin?.cvtEligible);

    const toggleNavItem = useCallback(
        (item: ExpandableNavItems, forceState?: boolean) => {
            setUserToggledNavItems((prev) => ({
                ...prev,
                [item]: typeof forceState === 'boolean' ? forceState : !isNavGroupOpen(item),
            }));
        },
        [isNavGroupOpen]
    );

    function handleNavigationTogglePress() {
        onOpenTogglePress(!open);
        setUserToggledNavItems({});
    }

    const fetchFeedbackSummary = useCallback(() => {
        return feedbackService
            .fetchFeedbackSummary({ signal: abortSignalRef.current })
            .then((response) => {
                if (!response.success && response.aborted) {
                    return;
                } else if (!response.success) {
                    throw response.data;
                }
                setFeedbackGroups(response.data.summary.feedbackGroups);
            })
            .catch((e) => {
                alert(e.message);
            });
    }, [abortSignalRef]);

    const fetchRecentSearches = useCallback(async () => {
        try {
            const response = await qmrsService.getAdvancedSearchRecentSavedSearches();
            if (response.success) {
                setRecentSavedSearches(response.data.savedSearches);
            } else {
                throw response.data;
            }
        } catch (error) {
            window.alert(error.message);
        }
    }, [setRecentSavedSearches]);

    const expandCollapsedNavigation = useCallback(
        (item: ExpandableNavItems) => {
            onOpenTogglePress(true);
            toggleNavItem(item, true);
        },
        [onOpenTogglePress, toggleNavItem]
    );

    useEffect(() => {
        if (hideNavigation) {
            return;
        }
        fetchFeedbackSummary();
        fetchQmrSummaries();
        fetchRecentSearches();
    }, [abortSignalRef, fetchFeedbackSummary, fetchQmrSummaries, fetchRecentSearches, hideNavigation]);

    const shouldFetchInvestigations = account?.investigationCapabilities?.viewInvestigations;
    const investigationsNavGroupOpen = isNavGroupOpen('investigations');

    useEffect(() => {
        if (shouldFetchInvestigations && !hideNavigation && investigationsNavGroupOpen) {
            investigationService
                .getInvestigations({
                    queryString: 'size=5',
                    signal: abortSignalRef.current,
                })
                .then((response) => {
                    if (!response.success && response.aborted) {
                        return;
                    } else if (!response.success) {
                        alert(response.data.message);
                        return;
                    }
                    setRecentInvestigations(response.data);
                })
                .catch((e) => {
                    logger.error('Failed to fetch recent investigations', { error: e });
                });
        }
    }, [abortSignalRef, shouldFetchInvestigations, hideNavigation, investigationsNavGroupOpen]);

    useEffect(() => {
        setDisplayFlag(vinSessionRouteMatch ? false : true);
    }, [vinSessionRouteMatch]);

    useInterval(
        () => {
            fetchNotificationsSummary({
                notificationsService,
                notificationsDispatch,
                signal: abortSignalRef.current,
                ignoreCache: true,
            });
        },
        1000 * 60 * 2
    ); // 2 minutes - interim fix while FCM web push is being investigated

    useEffect(() => {
        const currentNewCount = notificationSummary?.[NotificationStatusId.New]?.count;
        if (
            currentNewCount !== undefined &&
            prevNewCountRef.current !== undefined &&
            currentNewCount > prevNewCountRef.current
        ) {
            fetchNotificationsList({
                notificationsService,
                notificationsDispatch,
                signal: abortSignalRef.current,
                ignoreCache: true,
            });
        }
        prevNewCountRef.current = currentNewCount;
    }, [notificationSummary, notificationsDispatch, abortSignalRef]);

    const handleOnHover = (title: string, ref?: any) => {
        hoveredMap[title] = true;
        setHoveredMap(hoveredMap);
        setCurrentHover(title);
        setCurrentRef(ref);
    };
    const handleOnHoverLeave = (title: string) => {
        hoveredMap[title] = false;
        setHoveredMap(hoveredMap);
        setCurrentHover('');
    };

    if (hideNavigation) {
        return null;
    }

    return (
        <>
            <div className={classes.navigationOuter}>
                <nav className={classes.navigation}>
                    <NavigationItem
                        iconName="home"
                        to={selectedVin ? `/vin/${selectedVinString}` : '/'}
                        title={t('nav:dashboard')}
                        isActive={!!landingRouteMatch || !!vinDashboardRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        displayFlag={true}
                    />

                    {/* SSM5 */}
                    {isSsm5DiagnosticsFeatureEnabled && (
                        <NavigationItem
                            displayFlag={true}
                            iconName="connector"
                            isActive={isNavRouteActive('ssm5', pathname)}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            title={t('nav:ssm5Diagnostic')}
                            to={selectedVin ? `/vin/${selectedVinString}/ssm5` : '/ssm5'}
                        />
                    )}

                    {/* STIS Publications*/}
                    <NavigationItem
                        stisIconFlag={true}
                        to={selectedVin ? `/vin/${selectedVinString}/stis` : `/stis`}
                        title={t('nav:stisPublications')}
                        isActive={!!stisRouteMatch || !!stisAltRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        recordsCount={selectedVin ? publicationsCount : undefined}
                        displayFlag={canAccessStis || canAccessSTIS(account)}
                    />

                    {/* Service Diagnostics */}
                    <NavigationItem
                        customIcon={
                            <AccountManagerIcon
                                size={20}
                                color={serviceDiagnosticsRouteMatch ? colors.blueOne : colors.textDarkSecondary}
                            />
                        }
                        to="/service-diagnostics"
                        title={t('nav:serviceDiagnostics')}
                        isActive={Boolean(serviceDiagnosticsRouteMatch)}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        displayFlag={!selectedVin && Boolean(account) && (canAccessStis || canAccessSTIS(account))}
                    />

                    {canViewQmrs && (
                        <NavigationItem
                            iconName="car-subaru"
                            to={showQmrsDashboard ? '/qmrs/dashboard' : '/qmrs'}
                            title={t('nav:qmrs.title')}
                            isActive={showQmrsDashboard ? !!qmrsSlashDashboard || !!nqQMRRouteMatch : qaNavRouteActive}
                            isMainNavActive={!!qmrsSlashDashboard}
                            open={isNavGroupOpen('qmr')}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            onCollapseToggle={() => toggleNavItem('qmr')}
                            onClick={() => expandCollapsedNavigation('qmr')}
                            displayFlag={displayFlag}
                        >
                            <NavigationSubItem
                                key="All QMRs"
                                badge=""
                                isActive={
                                    (!!qmrRouteMatch || !!qmrWildcardRouteMatch) &&
                                    !advancedSearchRouteMatch &&
                                    !advancedSearchResultRouteMatch &&
                                    !advancedSearchSavedResultsRouteMatch &&
                                    !qmrsSlashDashboard
                                }
                                to="/qmrs"
                                title="Quality Monitoring Reports"
                            />
                            {isPrimaryObjectiveEnabled && (
                                <NavigationSubItem
                                    key="Non Quality Reports"
                                    badge=""
                                    isActive={!!nqQMRRouteMatch}
                                    to="/nqrs"
                                    title="Non Quality Reports"
                                />
                            )}
                            {account?.systemCapabilities?.canViewEscalations && (
                                <NavigationSubItem
                                    to="/escalations"
                                    title={t('nav:escalations', 'Escalations')}
                                    isActive={!!escalationsRouteMatch || !!escalationsWildcardRouteMatch}
                                />
                            )}
                            {account?.investigationCapabilities?.viewInvestigations && (
                                <NavigationSubItem
                                    to="/investigations"
                                    title={t('nav:investigations')}
                                    isActive={!!investigationsRouteMatch || !!investigationsWildcardRouteMatch}
                                >
                                    <div className="pl-4 pr-4">
                                        <hr className="mb-2" />
                                        <Typography variant="caption2" color="graySix">
                                            Recent
                                        </Typography>
                                    </div>
                                    {recentInvestigations.length > 0 &&
                                        recentInvestigations.map((investigation, index) => (
                                            <NavigationSubItem
                                                key={index}
                                                title={investigation.title}
                                                to={'/investigations/' + investigation.investigationId}
                                                isActive={
                                                    investigationsDetailRouteMatch
                                                        ? investigation.investigationId ===
                                                          investigationsDetailRouteMatch.params.id
                                                        : false
                                                }
                                            />
                                        ))}
                                </NavigationSubItem>
                            )}
                            <NavigationSubItem
                                to="/qmrs/advanced-search/search"
                                title="Advanced Search"
                                isActive={!!advancedSearchRouteMatch || !!advancedSearchResultRouteMatch}
                            />
                            <div className="pl-4 pr-4">
                                <button
                                    className={classes.navigationSubItemCollapseButton}
                                    onClick={() => {
                                        setSavedSearchesOpen(!savedSearchesOpen);
                                    }}
                                >
                                    <Icon
                                        name={savedSearchesOpen ? 'arrow-small-up' : 'arrow-small-down'}
                                        size={16}
                                        style={{ marginRight: 16 }}
                                    />
                                    <Typography>Saved Searches</Typography>
                                </button>
                            </div>
                            <Collapse in={savedSearchesOpen}>
                                <div>
                                    {recentSavedSearches.map((search) => {
                                        return (
                                            <NavigationSubItem
                                                key={search.savedSearchId}
                                                to={`/qmrs/advanced-search/saved/${search.savedSearchId}`}
                                                title={search.name}
                                                iconName="paragraph"
                                                isSubOfSub
                                                isActive={
                                                    advancedSearchSavedResultsRouteMatch
                                                        ? search.savedSearchId ===
                                                          advancedSearchSavedResultsRouteMatch.params.searchId
                                                        : false
                                                }
                                            />
                                        );
                                    })}
                                    <NavigationSubItem
                                        to="/qmrs/advanced-search/saved"
                                        title="View All"
                                        iconName="eye"
                                        isSubOfSub
                                        isActive={!!savedSearchRouteMatch}
                                    />
                                </div>
                            </Collapse>
                        </NavigationItem>
                    )}

                    {/* <NavigationItem
                        iconName="car-subaru"
                        to={`/vin/${selectedVinString}/qmrs`}
                        title={t('nav:qmrReports')}
                        isActive={!!vinQmrRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        displayFlag={vinSessionRouteMatch ? true : false}
                        recordsCount={vinQmrsCount}
                    /> */}

                    {canViewQmrs && (
                        <NavigationItem
                            iconName="car-subaru"
                            title={t('nav:qmrs.title')}
                            isMainNavActive={!!qmrsSlashDashboard}
                            open={isNavGroupOpen('vinQmr')}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            onCollapseToggle={() => toggleNavItem('vinQmr')}
                            onClick={() => expandCollapsedNavigation('vinQmr')}
                            displayFlag={vinSessionRouteMatch ? true : false}
                            recordsCount={vinQmrsCount.totalCount}
                        >
                            <NavigationSubItem
                                key="All QMRs"
                                badge=""
                                isActive={!!vinQmrRouteMatch}
                                to={`/vin/${selectedVinString}/qmrs`}
                                title="Quality Monitoring Reports"
                                recordsCount={`${vinQmrsCount.qmrCount}`}
                            />
                            {isPrimaryObjectiveEnabled && (
                                <NavigationSubItem
                                    key="Non Quality Reports"
                                    badge=""
                                    isActive={!!vinNqQMRRouteMatch}
                                    to={`/vin/${selectedVinString}/nqrs`}
                                    title="Non Quality Reports"
                                    recordsCount={`${vinQmrsCount.nqrCount}`}
                                />
                            )}
                            {account?.systemCapabilities?.canViewEscalations && (
                                <NavigationSubItem
                                    to={`/vin/${selectedVinString}/escalations`}
                                    title={t('nav:escalations', 'Escalations')}
                                    isActive={!!vinEscalationRouteMatch}
                                    recordsCount={`${vinQmrsCount.escalationCount}`}
                                />
                            )}
                            {account?.investigationCapabilities?.viewInvestigations && (
                                <NavigationSubItem
                                    to={`/vin/${selectedVinString}/investigations`}
                                    title={t('nav:investigations')}
                                    isActive={!!vinInvestigationsRouteMatch}
                                    recordsCount={`${vinQmrsCount.investigationCount}`}
                                />
                            )}
                        </NavigationItem>
                    )}

                    <NavigationItem
                        iconName="techline-logo"
                        to={`/vin/${selectedVinString}/techline`}
                        title={t('nav:techlineCases')}
                        isActive={!!vinTechlineRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        displayFlag={vinSessionRouteMatch ? true : false}
                    />

                    {canViewCidCvnCheck && (
                        <NavigationItem
                            iconName="ecm"
                            isActive={!!vinCidCvnCheckRouteMatch}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            title={t('nav:cidCvnCheck')}
                            to={`/vin/${selectedVinString}/cid-cvn-check`}
                            displayFlag={!!vinSessionRouteMatch}
                            recordsCount={cidCvnCount}
                        />
                    )}

                    {/* {Repair Order History} */}
                    <NavigationItem
                        iconName="repair-order"
                        isActive={!!vinRepairOrderRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        title={t('nav:repairOrder', 'Repair Order History')}
                        to={`/vin/${selectedVinString}/repair-orders`}
                        displayFlag={!!vinSessionRouteMatch}
                        recordsCount={dmsCount}
                    />

                    {/* BMIS */}
                    {canViewBmisInVinSession && (
                        <NavigationItem
                            iconName="battery"
                            to={`/vin/${selectedVinString}/bmis`}
                            title={t('nav:bmis')}
                            isActive={!!bmisRouteMatch}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            displayFlag={!!vinSessionRouteMatch}
                            recordsCount={bmisData?.length}
                        />
                    )}

                    {/* campaigns */}
                    <NavigationItem
                        iconName="recall-swap-driving"
                        isActive={!!vinCampaignsRouteMatch}
                        onHover={handleOnHover}
                        onHoverLeave={handleOnHoverLeave}
                        title={t('nav:campaigns', 'Campaigns')}
                        to={`/vin/${selectedVinString}/campaigns`}
                        displayFlag={!!vinSessionRouteMatch}
                        recordsCount={campaignCount}
                    />

                    {canViewTechline && (
                        <NavigationItem
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            iconName="phone"
                            to="/techline/dashboardv2"
                            title={t('nav:techline')}
                            isActive={!!techlineDashboardV2RouteMatch}
                            open={isNavGroupOpen('techline')}
                            onCollapseToggle={() => toggleNavItem('techline')}
                            onClick={() => expandCollapsedNavigation('techline')}
                            displayFlag={displayFlag}
                            isMainNavActive={!!techlineDashboardV2RouteMatch}
                        >
                            <NavigationSubItem
                                key="techline-cases"
                                to="/techline/dashboard"
                                badge=""
                                title={t('nav:cases')}
                                isActive={!!techlineRouteMatch}
                            />
                        </NavigationItem>
                    )}

                    {isVinCvtEligible && (
                        <NavigationItem
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            iconName="auto-cvt"
                            to={`/vin/${selectedVinString}/cvt`}
                            title={t('nav:cvt')}
                            isActive={!!cvtRouteMatch}
                            displayFlag={vinSessionRouteMatch ? true : false}
                            recordsCount={cvtList?.totalCount}
                            isAdminHub={true}
                        />
                    )}

                    {/* Commented out per SD-955 for UAT */}
                    {canViewFeedback && (
                        <NavigationItem
                            iconName="chat-bubble-text"
                            to="/feedback"
                            title={t('nav:feedback')}
                            isActive={!!feedbackRouteMatch || !!feedbackWildcardRouteMatch}
                            open={isNavGroupOpen('feedback')}
                            onCollapseToggle={() => toggleNavItem('feedback')}
                            onClick={() => expandCollapsedNavigation('feedback')}
                            onHover={handleOnHover}
                            onHoverLeave={handleOnHoverLeave}
                            displayFlag={displayFlag}
                        >
                            {feedbackGroups.map((feedbackGroup, idx) => {
                                let path = `/feedback?groupType=${feedbackGroup.feedbackGroupType}`;
                                if (feedbackGroup.entityId) {
                                    path += `&entityId=${feedbackGroup.entityId}`;
                                }
                                return (
                                    <NavigationSubItem
                                        key={idx}
                                        to={path}
                                        title={feedbackGroup.listDescription}
                                        badge={feedbackGroup.count > 0 ? feedbackGroup.countDescription : ''}
                                        isActive={
                                            feedbackGroup.entityId
                                                ? !!feedbackRouteMatch &&
                                                  feedbackGroup.feedbackGroupType === queryGroupType &&
                                                  (queryEntityId ? feedbackGroup.entityId === queryEntityId : false)
                                                : !!feedbackRouteMatch &&
                                                  feedbackGroup.feedbackGroupType === queryGroupType
                                        }
                                    />
                                );
                            })}
                        </NavigationItem>
                    )}
                    {/*
                    This badge is gated by the "feature-flag-demo" Unleash flag. It exists to:
                      1. Verify that Feature Flags are working as expected
                      2. Demonstrate to lay people what we can do with Feature Flags.
                      3. Demonstrate to developers how they can control Feature Flags during development.
                      4. Allow testing of Feature Flag configurations in a safe way. We can test different
                         strategies without worrying that a misconfiguration will expose something unexpected
                         to users (beyond this harmless badge in the menu).

                    This badge will remain useful, far into the future, so we should not remove it. It's
                    a permanent utility and it should be allowed to persist through to all environments.

                    - Luke C (4/7/2026) TECHSHARE-5481
                    */}
                    {isFeatureFlagDemoFeatureEnabled && open && (
                        <div className={classes.featureFlagDemoTag}>
                            <span style={{ color: '#D4A017', fontSize: 16, lineHeight: 1 }}>&#9733;</span>
                            Demo Flag Enabled
                        </div>
                    )}

                    {account?.systemCapabilities?.adminstrationNavigationEnabled && (
                        <div className="mt-auto">
                            <NavigationItem
                                iconName="administration"
                                title={t('Administration')}
                                to="/administration"
                                isActive={!!adminRouteMatch || !!adminWildcardRouteMatch}
                                onHover={handleOnHover}
                                onHoverLeave={handleOnHoverLeave}
                                isAdminHub={true}
                                displayFlag={displayFlag}
                            />
                        </div>
                    )}
                </nav>
                <div className={classes.collapseButtonOuter}>
                    <Button variant="link" onPress={handleNavigationTogglePress}>
                        <div className={classes.collapseButton}>
                            <Icon name={open ? 'chevron-left' : 'chevron-right'} color="blueOne" size={16} />
                        </div>
                    </Button>
                </div>
            </div>
            {currentHover && !open && <NavigationItemHoverTitle forwardRef={currentRef} title={currentHover} />}
        </>
    );
};
export default Navigation;
